import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Typography, message } from 'antd';
import { DownloadOutlined, LinkOutlined, MailOutlined, SendOutlined, WhatsAppOutlined } from '@ant-design/icons';

import {
  createQuotePublicLink,
  getQuotePdfUrl,
  notifyQuote,
} from '@erp/quotes/api/quotes.api';
import type { QuoteOut } from '@erp/quotes/types';
import {
  buildPublicQuoteUrl,
  copyTextToClipboard,
} from '@erp/quotes/utils/quoteFormatting';

interface QuotePostSaveActionsModalProps {
  quote: QuoteOut | null;
  canSend?: boolean;
  onClose: () => void;
}

type NotificationChannel = 'email' | 'whatsapp' | 'manual';

export const QuotePostSaveActionsModal: React.FC<QuotePostSaveActionsModalProps> = ({
  quote,
  canSend = false,
  onClose,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [isCreatingLink, setCreatingLink] = useState(false);
  const [isSending, setSending] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);

  useEffect(() => {
    setPublicUrl(null);
    setCreatingLink(false);
    setSending(false);
    setShowSendOptions(false);
  }, [quote?.id]);

  if (!quote) return null;

  const handleCopyLink = async () => {
    setCreatingLink(true);
    try {
      let url = publicUrl;
      if (!url) {
        const link = await createQuotePublicLink(quote.id);
        url = buildPublicQuoteUrl(window.location.origin, link.public_id, link.token);
        setPublicUrl(url);
      }
      const copyResult = await copyTextToClipboard(url);
      if (copyResult === 'copied') {
        messageApi.success('Link copiado.');
      } else {
        messageApi.warning('Seleccioná el link y presioná Ctrl+C para copiarlo.');
      }
    } catch {
      messageApi.error('No se pudo generar el link público.');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleNotify = async (channel: NotificationChannel) => {
    if (!canSend || isSending) return;
    setSending(true);
    try {
      await notifyQuote(quote.id, channel);
      messageApi.success(channel === 'email' ? 'Presupuesto enviado por email.' : channel === 'whatsapp' ? 'Presupuesto enviado por WhatsApp.' : 'Envío registrado.');
      setShowSendOptions(false);
    } catch {
      messageApi.error('No se pudo enviar el presupuesto.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open
      title={`Presupuesto #${quote.id} guardado`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Cerrar</Button>}
      destroyOnClose
    >
      {contextHolder}
      <Typography.Paragraph type="secondary">
        Elegí qué querés hacer con el presupuesto.
      </Typography.Paragraph>
      <Space wrap size={[8, 8]} className="pos-quote-actions-modal__actions">
        <Button
          icon={<SendOutlined />}
          loading={isSending}
          disabled={!canSend}
          onClick={() => setShowSendOptions((current) => !current)}
        >
          Enviar
        </Button>
        <Button icon={<LinkOutlined />} onClick={() => void handleCopyLink()} loading={isCreatingLink}>
          Copiar link
        </Button>
        <Button
          icon={<DownloadOutlined />}
          href={getQuotePdfUrl(quote.id)}
          target="_blank"
          rel="noreferrer"
        >
          Descargar PDF
        </Button>
      </Space>
      {showSendOptions && canSend ? (
        <Space wrap size={[8, 8]} className="pos-quote-actions-modal__send-options">
          <Button size="small" icon={<MailOutlined />} onClick={() => void handleNotify('email')}>
            Enviar por email
          </Button>
          <Button size="small" icon={<WhatsAppOutlined />} onClick={() => void handleNotify('whatsapp')}>
            Enviar por WhatsApp
          </Button>
          <Button size="small" icon={<SendOutlined />} onClick={() => void handleNotify('manual')}>
            Registrar envío manual
          </Button>
        </Space>
      ) : null}
      {publicUrl ? (
        <div className="pos-quote-actions-modal__link">
          <Typography.Text type="secondary">Link público</Typography.Text>
          <Input.TextArea
            value={publicUrl}
            readOnly
            autoFocus
            rows={2}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : null}
    </Modal>
  );
};

export default QuotePostSaveActionsModal;
